import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { IBlog, ICategory } from "types/interfaces/blog/IBlog";
import { Typography } from "@beamcloud/design-system";
import Logger from "services/logger";
import Category from "./components/Category";
import ArticleCard from "./components/ArticleCard";
import sanityClient from "./sanity";
import BannerSection from "../components/BannerSection";
import { useLocation } from "react-router-dom";

type IFilterType = ICategory | "All";

interface IProps {}

const Blog: React.FC<IProps> = () => {
  const [allBlogs, setAllBlogs] = useState<IBlog[]>([]);
  const [filteredBlogs, setFilteredBlogs] = useState<IBlog[]>([]);

  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const blogs: IBlog[] = await sanityClient.fetch(
          `
          *[_type == "blog"] | order(_createdAt desc) {
            "id": _id,
            title,
            description,
            author->{
              "id": _id,
              name,
              "image": picture
            },
            categories,
            coverImage,
            "slug": slug.current
          }
        `
        );
        setAllBlogs(blogs);
        setFilteredBlogs(blogs);
      } catch (error) {
        Logger.error("fetchAllBlogs", error);
      }
    })();
  }, []);

  const handleCategoryFilter = (categoryType: IFilterType) => {
    if (categoryType === "All") {
      setFilteredBlogs(allBlogs);
      return;
    }
    const filterdBlogs = allBlogs.filter(blog => blog.categories.includes(categoryType));
    setFilteredBlogs(filterdBlogs);
  };

  return (
    <Container>
      <TitleWrapper>
        <Helmet>
          <title>Blog • Beam</title>
          <link rel="canonical" href={`${window.location.origin}${location.pathname}`} />
          <meta
            name="description"
            content={
              "Ultrafast sandbox environments for AI agents. Bring your own custom image and run untrusted code safely – at scale."
            }
          />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={`Blog • Beam`} />
          <meta
            property="og:description"
            content={
              "Ultrafast sandbox environments for AI agents. Bring your own custom image and run untrusted code safely – at scale."
            }
          />
          <meta property="og:image" content={"https://www.beam.cloud/meta-hero.png"} />
          <meta property="og:url" content={window.location.href} />
          <meta name="twitter:title" content={`Blog • Beam`} />
          <meta
            name="twitter:description"
            content={
              "Ultrafast sandbox environments for AI agents. Bring your own custom image and run untrusted code safely – at scale."
            }
          />
          <meta name="twitter:image" content={"https://www.beam.cloud/meta-hero.png"} />
          <meta name="twitter:card" content="summary_large_image" />
        </Helmet>
        <Typography tag="h1" variant="title5Xl" color="gray100">
          Blog
        </Typography>
        <Categories>
          {(["All", "Product", "Company", "Tutorials", "Engineering"] as (ICategory | "All")[]).map(
            (categoryType, id) => (
              <CategoryFilter
                key={id}
                category={categoryType}
                onClick={() => handleCategoryFilter(categoryType)}
                size="base"
              />
            )
          )}
        </Categories>
      </TitleWrapper>
      <Articles>
        {filteredBlogs.map(blog => (
          <ArticleCard key={blog.id} {...blog} />
        ))}
      </Articles>
      <BannerSection />
    </Container>
  );
};

export default Blog;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 64px;
  align-items: center;
  padding-top: 48px;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 640px;
  gap: 24px;
  padding-left: 10px;
  padding-right: 10px;
`;

const Categories = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  padding: 0px;
  gap: 16px;
`;

const Articles = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0px;
  gap: 48px;
  max-width: 960px;

  @media (max-width: 959px) {
    justify-content: center;
  }
`;

const CategoryFilter = styled(Category)`
  cursor: pointer;
  &:hover {
    background-color: ${props => props.theme.colors.gray700};
  }
  &:active {
    background-color: ${props => props.theme.colors.gray600};
  }
`;
